import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* The shopper's cart, on /customer/cart — the routes read the customer off
   the bearer token (a 403 { code: "forbidden" } for anything else), and
   api/axios.js already attaches it, so none of these calls sets an
   Authorization header of its own any more.

   The service answers with a REAL HTTP status and a { code, message } body;
   a bare refusal carries no message, hence the fall through to the code. */
const errText = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

// ===============================
// ADD TO CART
// POST /customer/cart — JSON, not multipart: the route binds
// { productId, variantSku, variantLabel, quantity } and there is nothing to
// upload. Adding the same pack twice raises the quantity server-side rather
// than making a second line.
// ===============================
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ productId, variantSku, variantLabel, quantity = 1 }, { rejectWithValue }) => {
    try {
      const res = await api.post("/customer/cart", {
        productId,
        variantSku,
        variantLabel,
        quantity,
      });
      return res.data.line; // { id, quantity } — the upserted row, nothing more
    } catch (err) {
      return rejectWithValue(errText(err, "Could not add that to the cart"));
    }
  }
);

// ===============================
// GET USER CART
// GET /customer/cart — { lines, subtotalPaise, count }.
// storeId decides which store's price and availability each line carries.
// ===============================
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async ({ storeId = "" } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get(
        storeId ? `/customer/cart?storeId=${encodeURIComponent(storeId)}` : "/customer/cart"
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(errText(err, "Could not load the cart"));
    }
  }
);

// ===============================
// REMOVE CART ITEM
// There is no per-line DELETE — DELETE /customer/cart empties the WHOLE cart.
// One line is removed by setting its quantity to zero, which the PATCH route
// handles as a delete (the same thing the minus button does at 1).
// ===============================
export const removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async (id, { rejectWithValue }) => {
    try {
      await api.patch(`/customer/cart/${id}`, { quantity: 0 });
      return id;
    } catch (err) {
      return rejectWithValue(errText(err, "Could not remove that item"));
    }
  }
);

// ===============================
// UPDATE CART QUANTITY
// PATCH /customer/cart/{id} → { ok, removed }. The row itself does not come
// back, so the id and the quantity that were sent are what the reducer works
// from; `removed` is true when a quantity of zero or less deleted the line.
// The server caps a line at 99, so anything above that is stored as 99 there.
// ===============================
export const updateCartQuantity = createAsyncThunk(
  "cart/updateCartQuantity",
  async ({ id, quantity }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/customer/cart/${id}`, { quantity });
      /* the row does not come back, so what the reducer shows has to be what
         the server STORED: least(quantity, 99). Echoing the asked-for number
         would leave a line reading 150 against a cart the server holds at 99. */
      return { id, quantity: Math.min(Number(quantity), 99), removed: !!res.data?.removed };
    } catch (err) {
      return rejectWithValue(errText(err, "Could not update the quantity"));
    }
  }
);

// ===============================
// SLICE
// ===============================
const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: [],
    summary: {},
    loading: false,
    error: null
  },

  reducers: {
    clearCart: (state) => {
      state.items = [];
      state.summary = {};
    }
  },

  extraReducers: (builder) => {
    builder

      // ADD TO CART
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        /* the POST answers with { id, quantity } and nothing renderable — no
           name, image or price — so a line already on screen has its quantity
           corrected here and a brand new one waits for fetchCart rather than
           being pushed in as a blank row */
        const line = action.payload;
        const index = state.items.findIndex((item) => item.id === line?.id);
        if (index !== -1) state.items[index].quantity = line.quantity;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FETCH CART
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.lines || [];
        /* subtotalPaise is PAISE — divide by 100 wherever it is rendered */
        state.summary = {
          subtotalPaise: action.payload.subtotalPaise,
          count: action.payload.count,
        };
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // REMOVE ITEM
      .addCase(removeCartItem.fulfilled, (state, action) => {
        /* a cart line is keyed by `id` (a bigint) now — there is no _id */
        state.items = state.items.filter(
          (item) => item.id !== action.payload
        );
      })

      // UPDATE QUANTITY
      .addCase(updateCartQuantity.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCartQuantity.fulfilled, (state, action) => {
        state.loading = false;

        const { id, quantity, removed } = action.payload;

        if (removed) {
          state.items = state.items.filter((item) => item.id !== id);
          return;
        }

        const index = state.items.findIndex((item) => item.id === id);
        if (index !== -1) state.items[index].quantity = quantity;
      })
      .addCase(updateCartQuantity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearCart } = cartSlice.actions;
export default cartSlice.reducer;
